import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Container, Typography, Grid, Link } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import Loading from "../Layout/Loading";
import { useApi } from "../../utils/api";
import InstagramFeed from "./InstagramFeed";

const PublicCoachProfile = () => {
  const { slug } = useParams();
  const { getCoachProfilePublic } = useApi();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const p = await getCoachProfilePublic(slug);
      setProfile(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <Container sx={{ mt: 10 }}>
        <Loading />
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container sx={{ mt: 10 }}>
        <Typography variant="h6">Coach not found</Typography>
      </Container>
    );
  }

  const galleryImages = profile.galleryImages || [];
  const coachId = profile.coachId;

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", color: "#000" }}>
      {profile.bannerImageUrl && (
        <Box
          sx={{
            position: "relative",
            height: { xs: "45vh", md: "55vh" },
            overflow: "visible",
            mb: { xs: -8, md: -12 },
            zIndex: 1,
          }}
        >
          <img
            src={profile.bannerImageUrl}
            alt="Banner"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "grayscale(100%)",
            }}
          />
        </Box>
      )}

      <Container
        maxWidth="xl"
        sx={{
          pb: 8,
          px: { xs: 2, sm: 3, md: 4 },
          position: "relative",
          zIndex: 15,
        }}
      >
        <Box
          sx={{
            mb: { xs: 8, md: 10 },
            backgroundColor: "#fff",
            mt: { xs: 4, md: profile.bannerImageUrl ? 0 : 4 },
            p: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 4 },
            alignItems: { xs: "flex-start", md: "flex-start" },
            position: "relative",
            border: "3px solid #000",
            zIndex: 11,
          }}
        >
          {profile.imageUrl && (
            <Box
              sx={{
                width: { xs: 120, md: 180 },
                height: { xs: 120, md: 180 },
                borderRadius: "50%",
                border: "4px solid #000",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src={profile.imageUrl}
                alt={profile.displayName || profile.slug}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: 2,
                color: "#000",
              }}
            >
              {profile.displayName || profile.slug}
            </Typography>
            {profile.instagramUsername && (
              <Link
                href={`https://instagram.com/${profile.instagramUsername.replace(
                  "@",
                  ""
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  textDecoration: "none",
                  color: "inherit",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              >
                <InstagramIcon sx={{ color: "#E4405F" }} />
                <Typography variant="body1" sx={{ color: "#333" }}>
                  @{profile.instagramUsername.replace("@", "")}
                </Typography>
              </Link>
            )}
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "pre-wrap",
                color: "#333",
                lineHeight: 1.8,
                maxWidth: { md: "100%" },
              }}
            >
              {profile.bio || "Coach bio coming soon."}
            </Typography>
          </Box>
        </Box>

        {galleryImages.length > 0 && (
          <Box sx={{ mb: { xs: 10, md: 12 } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                mb: { xs: 4, md: 5 },
                textTransform: "uppercase",
                letterSpacing: 2,
                color: "#000",
              }}
            >
              Gallery
            </Typography>
            <Grid container spacing={3}>
              {galleryImages.map((url, index) => {
                const isLarge = index % 7 === 0 || index % 7 === 3;
                return (
                  <Grid
                    item
                    xs={12}
                    sm={isLarge ? 8 : 4}
                    md={isLarge ? 6 : 3}
                    key={index}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        border: "2px solid #000",
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                          zIndex: 1,
                        },
                      }}
                    >
                      <img
                        src={url.url || url}
                        alt={`Gallery ${index + 1}`}
                        style={{
                          width: "100%",
                          height: isLarge ? 280 : 180,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {profile.instagramUsername && (
          <Box sx={{ mb: { xs: 10, md: 12 } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                mb: { xs: 4, md: 5 },
                textTransform: "uppercase",
                letterSpacing: 2,
                color: "#000",
              }}
            >
              Instagram
            </Typography>
            <InstagramFeed userId={coachId} />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PublicCoachProfile;
